import { useState, useEffect } from 'react';
import TopNav from '../components/TopNav';
import { onboardingApi, authApi, settingsApi, ordersApi } from '../services/api';
import { useApp } from '../AppContext';

const SETTINGS_TABS = [
  { label: 'Company Profile',    icon: '🏢' },
  { label: 'User Management',    icon: '👥', hidden: true },
  { label: 'Billing Details',    icon: '🧾' },
  { label: 'Packages & Pricing', icon: '📦' },
  { label: 'Agreement / SLA',    icon: '📜' },
  { label: 'Change Password',    icon: '🔐' },
  { label: 'Notifications',      icon: '🔔' },
];

// tab index → onboarding liclass (only data tabs need API load)
const TAB_LICLASS = { 0: 1, 2: 3, 3: 4, 4: 7 };

const EMPLOYEE_SIZES = ['', '0-100', '201-500', '501-1,000', '1,001-5,000', '5,001-10,000', '10,000+'];
const PRICING_SUB_TABS = ['🛍️ A la Carte', '📦 Package', '➕ Extra Checks'];
const CONTRACT_ATTACHMENTS = ['Agreement Copy', 'PAN Copy', 'TAN Copy', 'GST Copy'];
const onlyDigits = e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); };

const STATIC_USERS = [
  { initials: 'RS', name: 'Rohit Sharma',   email: 'rohit.s@rajfinserv.in',     role: 'Admin',    lastLogin: 'Now',             status: 'Active' },
  { initials: 'AK', name: 'Ananya Kapoor',  email: 'ananya.k@rajfinserv.in',    role: 'Operator', lastLogin: '28 May, 9:45 AM', status: 'Active' },
  { initials: 'MP', name: 'Meera Patel',    email: 'accounts@rajfinserv.in',    role: 'Finance',  lastLogin: '25 May, 3:12 PM', status: 'Active' },
];
const STATIC_PENDING = [
  { email: 'hr@rajfinserv.in', role: 'Operator', invited: '26 May', expires: '02 Jun' },
];
const NOTIF_ITEMS = [
  { key: 'lowCredit',        label: 'Low Credit Alert',                desc: 'Notify when balance drops below ₹5,000'           },
  { key: 'orderStatus',      label: 'Order Status Updates',            desc: 'Email when any order status changes'              },
  { key: 'additionalCharge', label: 'Additional Charge Alerts',        desc: 'Immediate notification when approval is required' },
  { key: 'monthlyAnnexure',  label: 'Monthly Annexure Auto-email',     desc: 'Auto-email billing summary on 1st of each month'  },
  { key: 'inviteExpiry',     label: 'Candidate Invite Expiry Reminder', desc: 'Reminder 24 hrs before an invite expires'        },
];

function roleBadge(role) {
  const styles = {
    Admin:    { background: 'var(--nv)', color: '#fff' },
    Operator: { background: '#7c3aed', color: '#fff' },
    Finance:  { background: '#6d28d9', color: '#fff' },
  };
  const s = styles[role] || { background: 'var(--g200)', color: 'var(--g700)' };
  return (
    <span style={{ ...s, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: 20, fontSize: '11.5px', fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,.6)', display: 'inline-block' }} />
      {role}
    </span>
  );
}

function deriveCategory(checktype) {
  const n = (checktype || '').toLowerCase().trim();
  if (n.includes('address'))          return 'Address';
  if (n.startsWith('alcohol'))        return 'Alcohol';
  if (n.startsWith('criminal'))       return 'Criminal';
  if (n.startsWith('credential'))     return 'Credential';
  if (n.startsWith('database'))       return 'Database';
  if (n.startsWith('drug'))           return 'Drug Test';
  if (n === 'education')              return 'Education';
  if (n === 'directorship' || n.startsWith('employment') ||
      n === 'gap check'    || n === 'mca'     || n === 'psychometric' ||
      n === 'reference'    || n === 'resume'  ||
      n === 'right to work' || n === 'work authorization') return 'Employment';
  if (n === 'adverse media' || n === 'bank statement' || n.startsWith('bankruptcy') ||
      n === 'credit'        || n.startsWith('financial') ||
      n === 'form 16'       || n === 'form 26as')          return 'Finance';
  if (n.startsWith('identity') || n === 'uan' || n.startsWith('pan to')) return 'Identity';
  return 'Other';
}

function getCheckTypeSubLabel(checktype, category) {
  const ct = (checktype || '').trim();
  const cat = (category || '').trim();
  if (!ct || ct.toLowerCase() === cat.toLowerCase()) return null;
  const esc = cat.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const stripped = ct.replace(new RegExp(`^${esc}\\s*`, 'i'), '')
                      .replace(new RegExp(`\\s*${esc}$`, 'i'), '')
                      .trim();
  return stripped || ct;
}

function catChipClass(cat) {
  const map = {
    Address: 'cn', Alcohol: 'ca', Criminal: 'cr', Credential: 'cb',
    Database: 'cb', 'Drug Test': 'cpur', Education: 'cg', Employment: 'cg',
    Finance: 'cr', Identity: 'cb', Other: 'cgr',
  };
  return map[cat] || 'cn';
}

function contactTypeBadge(type) {
  const styles = {
    'Client POC':               { background: 'var(--nv)',  color: '#fff' },
    'Client Finance POC':       { background: '#6d28d9',    color: '#fff' },
    'Business Person':          { background: '#7c3aed',    color: '#fff' },
    'Client Signing Authority': { background: 'var(--or)',  color: '#fff' },
    'Cfirst Signing Authority': { background: 'var(--gr)',  color: '#fff' },
    'Contact Person':           { background: 'var(--bl)',  color: '#fff' },
  };
  const s = styles[type] || { background: 'var(--g200)', color: 'var(--g700)' };
  return (
    <span style={{ ...s, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: 20, fontSize: '11.5px', fontWeight: 600 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,.6)', display: 'inline-block' }} />
      {type}
    </span>
  );
}

export default function Settings({ onNavigate, initialTab, navTs }) {
  const { addToast, user, logout, setMinimumBalance, setInvitationExpireDay, clientId } = useApp();

  const [activeTab, setActiveTab] = useState(initialTab ?? 0);
  const [loading,   setLoading]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pageMsg,   setPageMsg]   = useState({ type: '', text: '' });
  const [apiData,   setApiData]   = useState({});

  // ── Tab 0 — Company Profile ────────────────────────────────
  const [t1, setT1] = useState({
    clientName: '', cinNumber: '', country: '', state: '',
    city: '', zipCode: '', industryType: '', employeeSize: '',
  });
  const [contacts, setContacts] = useState([]);

  // ── Tab 4 — Billing Details ────────────────────────────────
  const [t2, setT2] = useState({
    attnPersonName: '', department: '',
    billAddr1: '', billAddr2: '', billAddr3: '', billAddr4: '',
    billCountry: '', billState: '', billCity: '', billPostalCode: '',
    billGstin: '', billPan: '', billTan: '',
    sameAsBill: false,
    shipAddr1: '', shipAddr2: '', shipAddr3: '', shipAddr4: '',
    shipCountry: '', shipState: '', shipCity: '', shipPostalCode: '',
    shipGstin: '', shipPan: '', shipTan: '',
    billingAction: 'Add',
  });
  const [billNA, setBillNA]     = useState({ gstin: false, pan: false, tan: false });
  const [shipNA, setShipNA]     = useState({ gstin: false, pan: false, tan: false });
  const [t2Errors, setT2Errors] = useState({});

  // ── Country / State lists ──────────────────────────────────
  const [countries,  setCountries]  = useState([]);
  const [t1States,   setT1States]   = useState([]);
  const [billStates, setBillStates] = useState([]);
  const [shipStates, setShipStates] = useState([]);

  // ── Tab 5 — Pricing ────────────────────────────────────────
  const [pricingSubTab,      setPricingSubTab]      = useState(0);
  const [pricingAlacarte,    setPricingAlacarte]    = useState([]);
  const [pricingPackages,    setPricingPackages]    = useState([]);
  const [pricingExtraChecks, setPricingExtraChecks] = useState([]);
  const [checkTypeSettings,  setCheckTypeSettings]  = useState([]);
  const [alacarteSearch,     setAlacarteSearch]     = useState('');
  const [alacarteCategory,   setAlacarteCategory]   = useState('');

  // ── Tab 6 — Agreement / SLA ────────────────────────────────
  const [t4, setT4] = useState({
    contractId: '', contractType: '', startDate: '', endDate: '',
    remarks: '', contractAction: 'Add',
    hdncontractcopy: '', hdncontractcopyoriginal: '', hdncontractcopyext: '',
  });
  const [attachmentFiles,     setAttachmentFiles]     = useState({});
  const [attachmentUpload,    setAttachmentUpload]    = useState({});
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [downloading,         setDownloading]         = useState(false);

  // ── Tab 2 — Change Password ────────────────────────────────
  const [oldPwd,     setOldPwd]     = useState('');
  const [newPwd,     setNewPwd]     = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSubmitting, setPwdSubmitting] = useState(false);
  const [pwdError, setPwdError] = useState('');

  // ── Tab 3 — Notifications ──────────────────────────────────
  const [notifs, setNotifs] = useState({
    lowCredit: true, orderStatus: true, additionalCharge: true,
    monthlyAnnexure: true, inviteExpiry: false,
    reportInEmail: 'NO',
    reportPwProtected: 'NO',
    insuffNotification: 'NO',
    candidateReminder: 'N', reminderDays: '', reminderTill: '',
    isLoaDigiSignature: 'NO',
    multiFactorAuthClient: 'NO',
    multiFactorAuthCandidate: 'NO',
    lowBalanceEnabled: 'OFF', lowBalanceAmount: '',
    invitationExpireDay: '',
  });

  useEffect(() => {
    onboardingApi.getCountryList()
      .then(data => { if (Array.isArray(data)) setCountries(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (activeTab !== 6) return;
    settingsApi.getClientSettings()
      .then(res => {
        const d = res?.data;
        if (!d) return;
        setNotifs(s => ({
          ...s,
          reportInEmail:           d.report_in_email          || 'NO',
          reportPwProtected:       d.report_pw_protected      || 'NO',
          insuffNotification:      d.insuff_notification      || 'NO',
          candidateReminder:       d.candidate_reminder       || 'N',
          reminderDays:            d.reminder_days  != null   ? String(d.reminder_days)  : '',
          reminderTill:            d.reminder_till  != null   ? String(d.reminder_till)  : '',
          isLoaDigiSignature:      d.is_loa_digisignature     || 'NO',
          multiFactorAuthClient:   d.multifactor_auth_client  || 'NO',
          multiFactorAuthCandidate:d.multifactor_auth_candidate || 'NO',
          lowBalanceEnabled:       (d.minimum_balance || 0) > 0 ? 'ON' : 'OFF',
          lowBalanceAmount:        d.minimum_balance != null ? String(d.minimum_balance) : '',
          invitationExpireDay:     d.invitation_expire_day != null ? String(d.invitation_expire_day) : '',
        }));
      })
      .catch(() => {});
  }, [activeTab]);

  useEffect(() => {
    loadTab(initialTab ?? 0);
  }, [initialTab, navTs]);

  useEffect(() => {
    if (!t1.country) { setT1States([]); return; }
    onboardingApi.getStateList(t1.country)
      .then(data => { if (Array.isArray(data)) setT1States(data); })
      .catch(() => {});
  }, [t1.country]);

  useEffect(() => {
    if (!t2.billCountry) { setBillStates([]); return; }
    onboardingApi.getStateList(t2.billCountry)
      .then(data => { if (Array.isArray(data)) setBillStates(data); })
      .catch(() => {});
  }, [t2.billCountry]);

  useEffect(() => {
    if (!t2.shipCountry) { setShipStates([]); return; }
    onboardingApi.getStateList(t2.shipCountry)
      .then(data => { if (Array.isArray(data)) setShipStates(data); })
      .catch(() => {});
  }, [t2.shipCountry]);

  function loadTab(tabIndex) {
    clearMsg();
    const lc = TAB_LICLASS[tabIndex];
    if (!lc) { setActiveTab(tabIndex); return; }
    setLoading(true);
    setActiveTab(tabIndex);
    onboardingApi.getDetails(lc)
      .then(res => {
        const dataIsNull = res?.data == null;
        const d = res?.data || {};
        setApiData(d);
        prefill(d, dataIsNull, lc);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  function prefill(d, dataIsNull, lc) {
    if (lc === 1) {
      const cl = d.clients?.[0];
      if (cl) {
        setT1({
          clientName:   cl.clientname   || '',
          cinNumber:    cl.cinnumber    || '',
          country:      cl.country      || '',
          state:        cl.state        || '',
          city:         cl.city         || '',
          zipCode:      cl.zipcode      || '',
          industryType: cl.industrytype || '',
          employeeSize: cl.employeesize || '',
        });
      }
      const raw = d.contactssingle?.contacts || [];
      setContacts(raw.map(c => ({
        contactType: c.contacttype || '',
        contactName: c.contactname || '',
        designation: c.designation || '',
        email:       c.email       || '',
        phone:       c.phone       || '',
        mobile:      c.mobile      || '',
      })));
    }
    if (lc === 3) {
      const b  = d.clientbillinginfomodel;
      const pv = d.pantanvalidationvalue || '';
      if (b) {
        setT2(prev => ({
          ...prev,
          attnPersonName: b.attnpersonname  || '',
          department:     b.department      || '',
          billAddr1:      b.billtoaddress1  || '',
          billAddr2:      b.billtoaddress2  || '',
          billAddr3:      b.billtoaddress3  || '',
          billAddr4:      b.billtoaddress4  || '',
          billCountry:    b.billcountry     || '',
          billState:      b.billstate       || '',
          billCity:       b.billcity        || '',
          billPostalCode: b.billpostalcode  || '',
          billGstin:      b.gstinofbill     || '',
          billPan:        b.panofbill       || '',
          billTan:        b.tanofbill       || '',
          shipAddr1:      b.shiptoaddress1  || '',
          shipAddr2:      b.shiptoaddress2  || '',
          shipAddr3:      b.shiptoaddress3  || '',
          shipAddr4:      b.shiptoaddress4  || '',
          shipCountry:    b.shipcountry     || '',
          shipState:      b.shipstate       || '',
          shipCity:       b.shipcity        || '',
          shipPostalCode: b.shippostalcode  || '',
          shipGstin:      b.gstinofship     || '',
          shipPan:        b.panofship       || '',
          shipTan:        b.tanofship       || '',
          billingAction: 'Update',
        }));
        if (pv) {
          setBillNA({ gstin: b.gstinofbill === pv, pan: b.panofbill === pv, tan: b.tanofbill === pv });
          setShipNA({ gstin: b.gstinofship === pv, pan: b.panofship === pv, tan: b.tanofship === pv });
        }
      }
    }
    if (lc === 4) {
      setPricingAlacarte(d.pricinglist || []);
      setPricingPackages(d.pricinglistforpackage || []);
      setPricingExtraChecks(d.pricinglistforextrachecks || []);
      setCheckTypeSettings(d.checktypesettingslist || []);
    }
    if (lc === 7 && !dataIsNull) {
      const con = d.clientcontractmodel;
      if (con) {
        setT4(prev => ({
          ...prev,
          contractId:     con.id           || '',
          contractType:   con.contracttype || '',
          startDate:      con.startdate    || '',
          endDate:        con.enddate      || '',
          remarks:        con.remarks      || '',
          contractAction: 'Update',
        }));
        setExistingAttachments(con.attachment || []);
      }
    }
  }

  // ── Computed helpers ───────────────────────────────────────
  const panV     = apiData.pantanvalidationvalue || '';
  const panLbl   = apiData.pantanvalidationlabel || 'Not Applicable';
  const industry = Array.isArray(apiData.industrytype) ? apiData.industrytype : [];

  const packageGroups = Object.values(
    pricingPackages.filter(p => p.pricingtype === 'Package').reduce((acc, p) => {
      const key = p.packageid;
      if (!acc[key]) acc[key] = { name: p.packagename, price: p.price, countryname: p.countryname, checks: [] };
      acc[key].checks.push(p);
      return acc;
    }, {})
  );

  const pricingAlacarteWithCat = pricingAlacarte.map(p => ({
    ...p,
    category: p.category || deriveCategory(p.checktype),
  }));
  const alacarteCategories = [...new Set(pricingAlacarteWithCat.map(p => p.category))].sort();
  const filteredAlacarte = pricingAlacarteWithCat.filter(p => {
    const matchSearch = !alacarteSearch || (p.checktype || '').toLowerCase().includes(alacarteSearch.toLowerCase());
    const matchCat = !alacarteCategory || p.category === alacarteCategory;
    return matchSearch && matchCat;
  });
  const CARD_PRIORITY = ['Address', 'Criminal', 'Education', 'Employment'];
  const catMap = pricingAlacarteWithCat.reduce((acc, p) => {
    const cat = p.category;
    if (!acc[cat]) acc[cat] = { count: 0, price: p.price, sublabels: [] };
    acc[cat].count++;
    const sub = getCheckTypeSubLabel(p.checktype, cat);
    if (sub && acc[cat].sublabels.length < 5) acc[cat].sublabels.push(sub);
    return acc;
  }, {});
  const topAlaCarteCats = [
    ...CARD_PRIORITY.filter(c => catMap[c]).map(c => [c, catMap[c]]),
    ...Object.entries(catMap).filter(([c]) => !CARD_PRIORITY.includes(c)).sort((a, b) => b[1].count - a[1].count),
  ].slice(0, 4);
  const totalPkgCheckTypes = new Set(packageGroups.flatMap(pkg => pkg.checks.map(c => c.checktype))).size;
  const totalPkgValue = packageGroups.reduce((sum, pkg) => sum + (parseFloat(pkg.price) || 0), 0);
  const extraCountries = [...new Set(pricingExtraChecks.map(p => p.countryname).filter(Boolean))];
  const extraCurrencies = [...new Set(pricingExtraChecks.map(p => p.currency).filter(Boolean))];

  function setT1F(k, v) { setT1(p => ({ ...p, [k]: v })); clearMsg(); }
  function setT2F(k, v) { setT2(p => ({ ...p, [k]: v })); setT2Errors(p => { const n = { ...p }; delete n[k]; return n; }); clearMsg(); }
  function clearMsg()   { setPageMsg({ type: '', text: '' }); }

  async function saveNotifPreferences() {
    const invitationExpireDayNum = parseFloat(notifs.invitationExpireDay);
    if (!(invitationExpireDayNum >= 0)) {
      addToast('Invitation Expiry (Days) must be a positive number.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await settingsApi.updateClientSettings({
        REPORT_TYPE:                'PREBUILT',
        INSUFF_NOTIFICATION:        notifs.insuffNotification,
        REPORT_IN_EMAIL:            notifs.reportInEmail,
        CANDIDATE_REMINDER:         notifs.candidateReminder,
        REMINDER_DAYS:              parseFloat(notifs.reminderDays)   || 0,
        REMINDER_TILL:              parseFloat(notifs.reminderTill)   || 0,
        REPORT_PW_PROTECTED:        notifs.reportPwProtected,
        IS_LOA_DIGISIGNATURE:       notifs.isLoaDigiSignature,
        MULTIFACTOR_AUTH_CLIENT:    notifs.multiFactorAuthClient,
        MULTIFACTOR_AUTH_CANDIDATE: notifs.multiFactorAuthCandidate,
        MINIMUM_BALANCE:         notifs.lowBalanceEnabled === 'ON' ? (parseFloat(notifs.lowBalanceAmount) || 0) : 0,
        INVITATION_EXPIRE_DAY:   parseFloat(notifs.invitationExpireDay) || 0,
      });
      if (res.ok) {
        addToast(res.message, 'success');
        setMinimumBalance(notifs.lowBalanceEnabled === 'ON' ? (parseFloat(notifs.lowBalanceAmount) || 0) : 0);
        if ((parseFloat(notifs.invitationExpireDay) || 0) >= 0) {
          setInvitationExpireDay(parseFloat(notifs.invitationExpireDay));
        }
      } else {
        addToast(res.message, 'error');
      }
    } catch {
      addToast('Something went wrong. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function handleBillNA(field, checked) {
    setBillNA(p => ({ ...p, [field]: checked }));
    setT2F(`bill${field.charAt(0).toUpperCase() + field.slice(1)}`, checked ? panV : '');
  }
  function handleShipNA(field, checked) {
    setShipNA(p => ({ ...p, [field]: checked }));
    setT2F(`ship${field.charAt(0).toUpperCase() + field.slice(1)}`, checked ? panV : '');
  }

  function setAttachFile(type, file) {
    setAttachmentFiles(p => file ? { ...p, [type]: file } : (({ [type]: _, ...rest }) => rest)(p));
  }
  function toggleUpload(type, show) {
    setAttachmentUpload(p => ({ ...p, [type]: show }));
    if (!show) setAttachFile(type, null);
  }
  async function downloadExistingAttachment(fileName) {
    try {
      const { blob, filename } = await onboardingApi.downloadAttachment(fileName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { addToast('Download failed: ' + e.message, 'error'); }
  }
  async function downloadAgreement() {
    setDownloading(true);
    try {
      const { blob, filename } = await onboardingApi.downloadAgreement();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) { addToast('Download failed: ' + e.message, 'error'); }
    finally { setDownloading(false); }
  }

  // ── Submit: Company Profile ────────────────────────────────
  async function saveCompanyProfile() {
    if (!t1.clientName.trim()) { addToast('Client Name is required', 'error'); return; }
    setSubmitting(true); clearMsg();
    try {
      const body = {
        SalesClient: {},
        SalesClientContact: {
          ClientId: clientId ? Number(clientId) : 0,
          ContactType: [], ContactName: [], Designation: [], Email: [], Phone: [], Mobile: [],
        },
        ClientModel: {
          ClientId: clientId ? Number(clientId) : 0,
          State: t1.state, Country: t1.country,
          ClientType: 'PREPAID', CheckasperSLA: '',
          ClientBillingInfoModel: { ClientType: 'PREPAID' },
          Clients: [{
            ClientName: t1.clientName, CINnumber: t1.cinNumber,
            City: t1.city, Zipcode: t1.zipCode,
            IndustryType: t1.industryType, EmployeeSize: t1.employeeSize,
            CheckasperSLA: '',
          }],
        },
      };
      const { ok, data } = await onboardingApi.saveContactDetails(body);
      setPageMsg({ type: ok ? 'success' : 'error', text: data.message || (ok ? 'Saved successfully.' : 'Failed to save.') });
    } finally { setSubmitting(false); }
  }

  // ── Submit: Billing Details ────────────────────────────────
  async function saveBilling() {
    clearMsg();
    const sa = t2.sameAsBill;
    const bG = billNA.gstin ? panV : t2.billGstin;
    const bP = billNA.pan   ? panV : t2.billPan;
    const bT = billNA.tan   ? panV : t2.billTan;
    const sG = sa ? bG : (shipNA.gstin ? panV : t2.shipGstin);
    const sP = sa ? bP : (shipNA.pan   ? panV : t2.shipPan);
    const sT = sa ? bT : (shipNA.tan   ? panV : t2.shipTan);

    const errors = {};
    if (!String(t2.attnPersonName || '').trim()) errors.attnPersonName = 'Required';
    if (!String(t2.department     || '').trim()) errors.department     = 'Required';
    if (!String(t2.billAddr1      || '').trim()) errors.billAddr1      = 'Required';
    if (!String(t2.billAddr2      || '').trim()) errors.billAddr2      = 'Required';
    if (!String(t2.billCountry    || '').trim()) errors.billCountry    = 'Required';
    if (!String(t2.billState      || '').trim()) errors.billState      = 'Required';
    if (!String(t2.billCity       || '').trim()) errors.billCity       = 'Required';
    if (!String(t2.billPostalCode || '').trim()) errors.billPostalCode = 'Required';
    if (!String(bG || '').trim()) errors.billGstin = 'Required';
    if (!String(bP || '').trim()) errors.billPan   = 'Required';
    if (!String(bT || '').trim()) errors.billTan   = 'Required';
    if (!sa) {
      if (!String(t2.shipAddr1      || '').trim()) errors.shipAddr1      = 'Required';
      if (!String(t2.shipAddr2      || '').trim()) errors.shipAddr2      = 'Required';
      if (!String(t2.shipCountry    || '').trim()) errors.shipCountry    = 'Required';
      if (!String(t2.shipState      || '').trim()) errors.shipState      = 'Required';
      if (!String(t2.shipCity       || '').trim()) errors.shipCity       = 'Required';
      if (!String(t2.shipPostalCode || '').trim()) errors.shipPostalCode = 'Required';
      if (!String(sG || '').trim()) errors.shipGstin = 'Required';
      if (!String(sP || '').trim()) errors.shipPan   = 'Required';
      if (!String(sT || '').trim()) errors.shipTan   = 'Required';
    }
    const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}?$/;
    const PAN_RE   = /^[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}$/;
    const TAN_RE   = /^[a-zA-Z\d]{10}$/;
    if (!errors.billGstin && !billNA.gstin && bG && bG !== panV && !GSTIN_RE.test(bG)) errors.billGstin = 'Invalid GSTIN format';
    if (!errors.billPan   && !billNA.pan   && bP && bP !== panV && !PAN_RE.test(bP))   errors.billPan   = 'Invalid PAN format';
    if (!errors.billTan   && !billNA.tan   && bT && bT !== panV && !TAN_RE.test(bT))   errors.billTan   = 'Invalid TAN format';
    if (!sa) {
      if (!errors.shipGstin && !shipNA.gstin && sG && sG !== panV && !GSTIN_RE.test(sG)) errors.shipGstin = 'Invalid GSTIN format';
      if (!errors.shipPan   && !shipNA.pan   && sP && sP !== panV && !PAN_RE.test(sP))   errors.shipPan   = 'Invalid PAN format';
      if (!errors.shipTan   && !shipNA.tan   && sT && sT !== panV && !TAN_RE.test(sT))   errors.shipTan   = 'Invalid TAN format';
    }
    if (Object.keys(errors).length > 0) { setT2Errors(errors); return; }
    setT2Errors({});

    setSubmitting(true);
    try {
      const fd = new FormData();
      const B  = 'oClientBillingInfoModel.ClientBillingInfoModel';
      fd.append(`${B}.AttnPersonName`,  t2.attnPersonName);
      fd.append(`${B}.Department`,      t2.department);
      fd.append(`${B}.BillToAddress1`,  t2.billAddr1);
      fd.append(`${B}.BillToAddress2`,  t2.billAddr2);
      fd.append(`${B}.BillToAddress3`,  t2.billAddr3);
      fd.append(`${B}.BillToAddress4`,  t2.billAddr4);
      fd.append(`${B}.BillCountry`,     t2.billCountry);
      fd.append(`${B}.BillState`,       t2.billState);
      fd.append(`${B}.BillCity`,        t2.billCity);
      fd.append(`${B}.BillPostalCode`,  t2.billPostalCode);
      fd.append(`${B}.GSTINofBill`,     bG);
      fd.append(`${B}.PANofBill`,       bP);
      fd.append(`${B}.TANofBill`,       bT);
      fd.append(`${B}.ShipToAddress1`,  sa ? t2.billAddr1      : t2.shipAddr1);
      fd.append(`${B}.ShipToAddress2`,  sa ? t2.billAddr2      : t2.shipAddr2);
      fd.append(`${B}.ShipToAddress3`,  sa ? t2.billAddr3      : t2.shipAddr3);
      fd.append(`${B}.ShipToAddress4`,  sa ? t2.billAddr4      : t2.shipAddr4);
      fd.append(`${B}.ShipCountry`,     sa ? t2.billCountry    : t2.shipCountry);
      fd.append(`${B}.ShipState`,       sa ? t2.billState      : t2.shipState);
      fd.append(`${B}.ShipCity`,        sa ? t2.billCity       : t2.shipCity);
      fd.append(`${B}.ShipPostalCode`,  sa ? t2.billPostalCode : t2.shipPostalCode);
      fd.append(`${B}.GSTINofShip`,     sG);
      fd.append(`${B}.PANofShip`,       sP);
      fd.append(`${B}.TANofShip`,       sT);
      const { ok, data } = await onboardingApi.submitBillingInfo(
        { ClientId: clientId, BillingAction: t2.billingAction || 'Add' },
        fd,
      );
      if (ok) {
        setPageMsg({ type: 'success', text: data.message || 'Billing details saved successfully.' });
      } else {
        const errText = data.errors
          ? Object.entries(data.errors).map(([k, v]) => `${k}: ${v}`).join(' | ')
          : (data.message || data.title || 'Failed to save.');
        setPageMsg({ type: 'error', text: errText });
      }
    } finally { setSubmitting(false); }
  }

  // ── Submit: Agreement / SLA ────────────────────────────────
  async function saveContract() {
    setSubmitting(true); clearMsg();
    try {
      const params = {
        ClientId: clientId,
        ContractAction: t4.contractAction,
        hdncontractcopy: t4.hdncontractcopy,
        hdncontractcopyoriginal: t4.hdncontractcopyoriginal,
        hdncontractcopyext: t4.hdncontractcopyext,
      };
      const fd = new FormData();
      fd.append('oClientContractModel.Id',           t4.contractId || '');
      fd.append('oClientContractModel.ContractType', t4.contractType);
      fd.append('oClientContractModel.StartDate',    t4.startDate);
      if (t4.endDate) fd.append('oClientContractModel.EndDate', t4.endDate);
      fd.append('oClientContractModel.Remarks',      t4.remarks);
      fd.append('CFAttachment', CONTRACT_ATTACHMENTS.join(','));
      CONTRACT_ATTACHMENTS.forEach((type, i) => {
        const file     = attachmentFiles[type];
        const existing = existingAttachments.find(a => a.attachment_type === type);
        if (file) fd.append(`Attachment_${i}`, file);
        fd.append('ATTACHMENT_NAME', file ? file.name : (existing?.attachment_name || ''));
        fd.append('ATTACHMENT_ID',   existing?.attachment_id || '');
      });
      const { ok, data } = await onboardingApi.submitContractInfo(params, fd);
      setPageMsg({ type: ok ? 'success' : 'error', text: data.message || (ok ? 'Saved successfully.' : 'Failed to save.') });
    } finally { setSubmitting(false); }
  }

  // ── Submit: Change Password ────────────────────────────────
  const pwdAllFilled = oldPwd && newPwd && confirmPwd;
  const pwdMatched   = newPwd && confirmPwd && newPwd === confirmPwd;
  const pwdMismatch  = newPwd && confirmPwd && newPwd !== confirmPwd;
  const canSubmitPwd = pwdAllFilled && pwdMatched && !pwdSubmitting;

  async function handleChangePassword() {
    if (!pwdAllFilled) { addToast('All fields are required', 'error'); return; }
    if (!pwdMatched)   { addToast('New password and confirm password do not match', 'error'); return; }
    setPwdError('');
    setPwdSubmitting(true);
    try {
      const res = await authApi.changePassword(oldPwd, confirmPwd);
      if (res?.page === 'Login') {
        addToast(res.message || 'Password changed successfully!', 'success');
        await logout();
        onNavigate('login');
      } else {
        setPwdError(res?.message || 'Password change failed. Please try again.');
      }
    } catch (e) {
      setPwdError(e.message || 'Password change failed. Please try again.');
    } finally { setPwdSubmitting(false); }
  }

  // ── Render ─────────────────────────────────────────────────
  return (
    <>
      <TopNav currentPage="settings" onNavigate={onNavigate} />
      <div className="pg">
        <div className="phd">
          <div>
            <div className="ptitle">Account Settings</div>
            <div className="psub">Manage your company profile, users, and preferences</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'start', gap: '20px' }}>

          {/* ── Left Nav ────────────────────────────────── */}
          <div className="snav">
            {SETTINGS_TABS.map((t, i) => !t.hidden && (
              <div
                key={t.label}
                className={`sni ${activeTab === i ? 'on' : ''}`}
                style={{ cursor: 'pointer', minWidth: 0 }}
                onClick={() => loadTab(i)}
              >
                <span style={{ fontSize: '15px' }}>{t.icon}</span>
                <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.label}</span>
              </div>
            ))}
          </div>

          {/* ── Right Content ────────────────────────────── */}
          <div>
            {pageMsg.text && (
              <div className={`alert ${pageMsg.type === 'success' ? 'a-gr' : 'a-rd'} mb16`}>
                <div className="aico">{pageMsg.type === 'success' ? '✅' : '⚠'}</div>
                <div className="abody">{pageMsg.text}</div>
              </div>
            )}

            {/* Company Profile */}
            {activeTab === 0 && (
              <div className="scontent">
                {loading ? <LoadingBlock /> : (
                  <>
                    {/* Company header */}
                    <div className="flex aic g12" style={{ marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid var(--g100)' }}>
                      <div style={{
                        width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                        background: 'linear-gradient(135deg, var(--nv), var(--nv2))',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '17px', fontWeight: 800, color: '#fff',
                      }}>
                        {(t1.clientName || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--nv)' }}>{t1.clientName || '—'}</div>
                        <div style={{ fontSize: '12px', color: 'var(--g400)', marginTop: '2px' }}>
                          {[t1.city, t1States.find(s => String(s.value) === String(t1.state))?.text, countries.find(c => String(c.value) === String(t1.country))?.text].filter(Boolean).join(', ') || '—'}
                        </div>
                      </div>
                    </div>

                    {/* Info rows */}
                    <div>
                      {[
                        { label: 'CIN Number',   value: t1.cinNumber },
                        { label: 'Industry',     value: (industry.find(o => String(o.value ?? o.Value) === String(t1.industryType))?.text) || (industry.find(o => String(o.value ?? o.Value) === String(t1.industryType))?.Text) || t1.industryType },
                        { label: 'Employee Size', value: t1.employeeSize },
                        { label: 'Country',      value: countries.find(c => String(c.value) === String(t1.country))?.text || t1.country },
                        { label: 'State',        value: t1States.find(s => String(s.value) === String(t1.state))?.text || t1.state },
                        { label: 'City',         value: t1.city },
                        { label: 'Zip Code',     value: t1.zipCode },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex aic" style={{ padding: '6px 0' }}>
                          <div style={{ width: '140px', flexShrink: 0, fontSize: '12px', fontWeight: 600, color: 'var(--g400)' }}>{label} :</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--nv)' }}>{value || '—'}</div>
                        </div>
                      ))}
                    </div>

                    {/* Contacts */}
                    {contacts.length > 0 && (
                      <div style={{ marginTop: '20px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '12px' }}>Contact Persons</div>
                        <div className="twrap">
                          <table>
                            <thead>
                              <tr>
                                <th>CONTACT</th>
                                <th>CONTACT TYPE</th>
                                <th>DESIGNATION</th>
                                <th>PHONE</th>
                                <th>MOBILE</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contacts.map((c, i) => {
                                const words = (c.contactName || '').trim().split(/\s+/);
                                const initials = words.length >= 2
                                  ? words[0][0] + words[words.length - 1][0]
                                  : (words[0]?.[0] || '?');
                                const avatarBg = ['#3b82f6', '#a855f7', '#8b5cf6', '#0ea5e9', '#6366f1'][i % 5];
                                return (
                                  <tr key={i}>
                                    <td>
                                      <div className="flex aic g8">
                                        <div style={{
                                          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                          background: avatarBg, display: 'flex', alignItems: 'center',
                                          justifyContent: 'center', fontSize: '12px', fontWeight: 700, color: '#fff',
                                        }}>
                                          {initials.toUpperCase()}
                                        </div>
                                        <div>
                                          <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--nv)' }}>{c.contactName || '—'}</div>
                                          <div style={{ fontSize: '11px', color: 'var(--g400)' }}>{c.email || ''}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td>{contactTypeBadge(c.contactType)}</td>
                                    <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{c.designation || '—'}</td>
                                    <td style={{ fontSize: '12px', color: 'var(--g600)', fontFamily: 'monospace' }}>{c.phone || '—'}</td>
                                    <td style={{ fontSize: '12px', color: 'var(--g600)', fontFamily: 'monospace' }}>{c.mobile || '—'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* User Management */}
            {activeTab === 1 && (
              <div className="scontent">
                <div className="flex jb aic mb16">
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)' }}>👥 User Management</div>
                  <button className="btn bpri bsm">+ Invite User</button>
                </div>

                {/* Role legend */}
                <div className="flex g8 mb16" style={{ flexWrap: 'wrap' }}>
                  {[
                    { role: 'Admin',    desc: 'Full access incl. user mgmt.' },
                    { role: 'Operator', desc: 'Place orders & view reports' },
                    { role: 'Finance',  desc: 'Wallet, invoices & billing only' },
                  ].map(r => (
                    <div key={r.role} className="flex aic g8" style={{ fontSize: '12px', color: 'var(--g600)' }}>
                      {roleBadge(r.role)}
                      <span>{r.desc}</span>
                    </div>
                  ))}
                </div>

                {/* Users table */}
                <div className="twrap">
                  <table>
                    <thead>
                      <tr>
                        <th>USER</th>
                        <th>ROLE</th>
                        <th>LAST LOGIN</th>
                        <th>STATUS</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {STATIC_USERS.map((u, i) => (
                        <tr key={i}>
                          <td>
                            <div className="flex aic g8">
                              <div style={{
                                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                                background: ['#3b82f6','#a855f7','#8b5cf6'][i % 3],
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '12px', fontWeight: 700, color: '#fff',
                              }}>
                                {u.initials}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--nv)' }}>{u.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--g400)' }}>{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td>{roleBadge(u.role)}</td>
                          <td style={{ color: 'var(--g600)', fontSize: '12px' }}>{u.lastLogin}</td>
                          <td>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '12px', fontWeight: 600, color: 'var(--gr)' }}>
                              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gr)', display: 'inline-block' }} />
                              {u.status}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button className="btn bghost bsm" style={{ color: 'var(--or-dk)' }}>Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pending Invites */}
                {STATIC_PENDING.length > 0 && (
                  <div style={{ marginTop: '24px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '12px' }}>Pending Invites</div>
                    <div className="twrap">
                      <table>
                        <thead>
                          <tr>
                            <th>EMAIL</th>
                            <th>ROLE</th>
                            <th>INVITED</th>
                            <th>EXPIRES</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {STATIC_PENDING.map((p, i) => (
                            <tr key={i}>
                              <td style={{ color: 'var(--or-dk)', fontSize: '13px' }}>{p.email}</td>
                              <td>{roleBadge(p.role)}</td>
                              <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.invited}</td>
                              <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.expires}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button className="btn bghost bsm" style={{ color: 'var(--or-dk)', fontWeight: 600 }}>Resend</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Change Password */}
            {activeTab === 5 && (
              <div style={{ maxWidth: 480 }}>
                <div className="card">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {(user?.username || user?.email) && (
                      <div className="fg">
                        <label className="flbl">Username</label>
                        <input className="inp" value={user?.username || user?.email || ''} disabled
                          style={{ background: 'var(--g50)', color: 'var(--g500)', cursor: 'not-allowed' }} />
                      </div>
                    )}

                    <div className="fg">
                      <label className="flbl">Current Password <span className="req">*</span></label>
                      <input className="inp" type="password" placeholder="Enter current password"
                        value={oldPwd} onChange={e => setOldPwd(e.target.value)} />
                    </div>

                    <div className="fg">
                      <label className="flbl">New Password <span className="req">*</span></label>
                      <input className="inp" type="password" placeholder="Enter new password"
                        value={newPwd} onChange={e => setNewPwd(e.target.value)} />
                    </div>

                    <div className="fg">
                      <label className="flbl">Confirm New Password <span className="req">*</span></label>
                      <input className="inp" type="password" placeholder="Re-enter new password"
                        value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                        style={pwdMismatch ? { borderColor: 'var(--rd)', boxShadow: '0 0 0 2px rgba(229,62,62,.15)' }
                                           : pwdMatched ? { borderColor: 'var(--gr)' } : {}} />
                      {pwdMismatch && <div style={{ fontSize: 11.5, color: 'var(--rd)', marginTop: 4, fontWeight: 600 }}>Passwords do not match!</div>}
                      {pwdMatched  && <div style={{ fontSize: 11.5, color: 'var(--gr)', marginTop: 4, fontWeight: 600 }}>Passwords matched ✓</div>}
                    </div>

                    {pwdError && (
                      <div style={{ background: 'rgba(220,38,38,.07)', border: '1px solid rgba(220,38,38,.25)', borderRadius: 7, padding: '10px 14px', fontSize: 12.5, color: 'var(--rd)', fontWeight: 500 }}>
                        ⚠ {pwdError}
                      </div>
                    )}

                    <div className="flex g8 mt8">
                      <button className="btn bpri" onClick={handleChangePassword} disabled={!canSubmitPwd}
                        style={!canSubmitPwd ? { opacity: 0.45, cursor: 'not-allowed' } : {}}>
                        {pwdSubmitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : '🔒 Update Password'}
                      </button>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {/* Notifications */}
            {activeTab === 6 && (
              <div className="scontent">
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)', marginBottom: '16px' }}>Notification Preferences</div>
                {/* {NOTIF_ITEMS.map(n => (
                  <div key={n.key} className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>{n.label}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--g400)', marginTop: '2px' }}>{n.desc}</div>
                    </div>
                    <label className="toggle">
                      <input type="checkbox" checked={notifs[n.key]} onChange={e => setNotifs(s => ({ ...s, [n.key]: e.target.checked }))} />
                      <span className="tsl" />
                    </label>
                  </div>
                ))} */}
                {/* Report in Email */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Report in Email</div>
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.reportInEmail === 'YES'}
                      onChange={e => setNotifs(s => ({ ...s, reportInEmail: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>

                {/* Password In Report (PDF) */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Password In Report (PDF)</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.reportPwProtected === 'YES'}
                      onChange={e => setNotifs(s => ({ ...s, reportPwProtected: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>

                {/* Low Balance Alert */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Low Balance Alert</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.lowBalanceEnabled === 'ON'}
                      onChange={e => setNotifs(s => ({ ...s, lowBalanceEnabled: e.target.checked ? 'ON' : 'OFF', lowBalanceAmount: e.target.checked ? s.lowBalanceAmount : '' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>
                {notifs.lowBalanceEnabled === 'ON' && (
                  <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Minimum Balance</div>
                    <input
                      className="inp"
                      type="number" min="0"
                      value={notifs.lowBalanceAmount}
                      onChange={e => setNotifs(s => ({ ...s, lowBalanceAmount: e.target.value }))}
                      style={{ width: '90px', textAlign: 'center' }}
                    />
                  </div>
                )}

                {/* Insuff Notification */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Insuff Notification</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.insuffNotification === 'YES'}
                      onChange={e => setNotifs(s => ({ ...s, insuffNotification: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>

                {/* Candidate Reminder */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Candidate Reminder</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.candidateReminder === 'Y'}
                      onChange={e => setNotifs(s => ({ ...s, candidateReminder: e.target.checked ? 'Y' : 'N', reminderDays: e.target.checked ? s.reminderDays : '', reminderTill: e.target.checked ? s.reminderTill : '' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>
                {notifs.candidateReminder === 'Y' && (
                  <>
                    <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Reminder Days</div>
                      <input
                        className="inp"
                        type="number" min="1"
                        value={notifs.reminderDays}
                        onChange={e => setNotifs(s => ({ ...s, reminderDays: e.target.value }))}
                        style={{ width: '90px', textAlign: 'center' }}
                      />
                    </div>
                    <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Reminder Till</div>
                      <input
                        className="inp"
                        type="number" min="1"
                        value={notifs.reminderTill}
                        onChange={e => setNotifs(s => ({ ...s, reminderTill: e.target.value }))}
                        style={{ width: '90px', textAlign: 'center' }}
                      />
                    </div>
                  </>
                )}

                {/* Invitation Expiry */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Invitation Expiry (Days)</div>
                  <input
                    className="inp"
                    type="number" min="0"
                    value={notifs.invitationExpireDay}
                    onChange={e => {
                      const v = e.target.value;
                      if (v !== '' && Number(v) < 0) return;
                      setNotifs(s => ({ ...s, invitationExpireDay: v }));
                    }}
                    style={{ width: '90px', textAlign: 'center' }}
                  />
                </div>

                {/* Is LOA with Digital Signature */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Is LOA with Digital Signature</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.isLoaDigiSignature === 'YES'}
                      onChange={e => setNotifs(s => ({ ...s, isLoaDigiSignature: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>

                {/* Multi Auth For Client Login */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Multi Auth For Client Login</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.multiFactorAuthClient === 'YES'}
                      onChange={e => setNotifs(s => ({ ...s, multiFactorAuthClient: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>

                {/* Multi Auth For Candidate Login */}
                <div className="flex jb aic" style={{ padding: '12px 0', borderBottom: '1px solid var(--g100)' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--nv)' }}>Multi Auth For Candidate Login</div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={notifs.multiFactorAuthCandidate === 'YES'}
                      onChange={e => setNotifs(s => ({ ...s, multiFactorAuthCandidate: e.target.checked ? 'YES' : 'NO' }))}
                    />
                    <span className="tsl" />
                  </label>
                </div>

                <div className="flex mt16" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn bpri" disabled={submitting} onClick={saveNotifPreferences}>
                    {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : '💾 Save Preferences'}
                  </button>
                </div>
              </div>
            )}

            {/* Billing Details */}
            {activeTab === 2 && (
              <div className="scontent">
                {loading ? <LoadingBlock /> : (
                  <>
                    {(() => {
                      const infoRow = (label, value) => (
                        <div key={label} className="flex aic" style={{ padding: '6px 0' }}>
                          <div style={{ width: '130px', flexShrink: 0, fontSize: '12px', fontWeight: 600, color: 'var(--g400)' }}>{label} :</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--nv)' }}>{value || '—'}</div>
                        </div>
                      );
                      const billCountryText = countries.find(c => String(c.value) === String(t2.billCountry))?.text || t2.billCountry;
                      const billStateText   = billStates.find(s => String(s.value) === String(t2.billState))?.text   || t2.billState;
                      const shipCountryText = countries.find(c => String(c.value) === String(t2.shipCountry))?.text || t2.shipCountry;
                      const shipStateText   = shipStates.find(s => String(s.value) === String(t2.shipState))?.text   || t2.shipState;
                      return (
                        <>
                          {/* Billing Details header */}
                          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid var(--g100)' }}>Billing Details</div>
                          {infoRow('Attn. Person', t2.attnPersonName)}
                          {infoRow('Department',   t2.department)}

                          {/* Bill To + Ship To side by side */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 32px', marginTop: '16px' }}>
                            {/* Bill To */}
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid var(--g100)' }}>Bill To</div>
                              {infoRow('Address 1',   t2.billAddr1)}
                              {infoRow('Address 2',   t2.billAddr2)}
                              {infoRow('Address 3',   t2.billAddr3)}
                              {infoRow('Address 4',   t2.billAddr4)}
                              {infoRow('Country',     billCountryText)}
                              {infoRow('State',       billStateText)}
                              {infoRow('City',        t2.billCity)}
                              {infoRow('Postal Code', t2.billPostalCode)}
                              {infoRow('GSTIN',       t2.billGstin)}
                              {infoRow('PAN',         t2.billPan)}
                              {infoRow('TAN',         t2.billTan)}
                            </div>

                            {/* Ship To */}
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid var(--g100)' }}>
                                Ship To {t2.sameAsBill && <span style={{ fontSize: '11px', fontWeight: 400, color: 'var(--g400)' }}>(same as Bill To)</span>}
                              </div>
                              {t2.sameAsBill ? (
                                <>
                                  {infoRow('Address 1',   t2.billAddr1)}
                                  {infoRow('Address 2',   t2.billAddr2)}
                                  {infoRow('Address 3',   t2.billAddr3)}
                                  {infoRow('Address 4',   t2.billAddr4)}
                                  {infoRow('Country',     billCountryText)}
                                  {infoRow('State',       billStateText)}
                                  {infoRow('City',        t2.billCity)}
                                  {infoRow('Postal Code', t2.billPostalCode)}
                                  {infoRow('GSTIN',       t2.billGstin)}
                                  {infoRow('PAN',         t2.billPan)}
                                  {infoRow('TAN',         t2.billTan)}
                                </>
                              ) : (
                                <>
                                  {infoRow('Address 1',   t2.shipAddr1)}
                                  {infoRow('Address 2',   t2.shipAddr2)}
                                  {infoRow('Address 3',   t2.shipAddr3)}
                                  {infoRow('Address 4',   t2.shipAddr4)}
                                  {infoRow('Country',     shipCountryText)}
                                  {infoRow('State',       shipStateText)}
                                  {infoRow('City',        t2.shipCity)}
                                  {infoRow('Postal Code', t2.shipPostalCode)}
                                  {infoRow('GSTIN',       t2.shipGstin)}
                                  {infoRow('PAN',         t2.shipPan)}
                                  {infoRow('TAN',         t2.shipTan)}
                                </>
                              )}
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* Packages & Pricing */}
            {activeTab === 3 && (
              <div className="scontent">
                {loading ? <LoadingBlock /> : (
                  <>
                    {/* Header */}
                    <div className="flex jb aic" style={{ marginBottom: '16px' }}>
                      <div>
                        <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--nv)' }}>🏷️ Packages & Pricing</div>
                        <div style={{ fontSize: '12.5px', color: 'var(--g600)', marginTop: '2px' }}>Your configured check rates, packages and add-ons</div>
                      </div>
                    </div>

                    {/* Sub-tab bar */}
                    <div className="tabbar" style={{ marginBottom: '20px' }}>
                      {PRICING_SUB_TABS.map((t, i) => (
                        <div key={t} className={`tab ${pricingSubTab === i ? 'on' : ''}`} onClick={() => { setPricingSubTab(i); setAlacarteSearch(''); setAlacarteCategory(''); }}>{t}</div>
                      ))}
                    </div>

                    {/* ── A la Carte ── */}
                    {pricingSubTab === 0 && (
                      <>
                        {/* Category tier cards */}
                        {topAlaCarteCats.length > 0 && (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                            {topAlaCarteCats.map(([cat, info]) => (
                              <div
                                key={cat}
                                onClick={() => setAlacarteCategory(c => c === cat ? '' : cat)}
                                style={{
                                  border: `1.5px solid ${alacarteCategory === cat ? 'var(--or)' : 'var(--g200)'}`,
                                  borderRadius: 'var(--r)', padding: '14px 15px', cursor: 'pointer',
                                  background: alacarteCategory === cat ? 'var(--or-xlt)' : 'var(--wh)',
                                  transition: 'all .15s',
                                  boxShadow: alacarteCategory === cat ? '0 2px 10px rgba(245,166,35,.15)' : 'none',
                                }}
                              >
                                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '5px' }}>
                                  {info.count} check{info.count !== 1 ? 's' : ''}
                                </div>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '4px' }}>{cat}</div>
                                <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--or-dk)' }}>₹{info.price}</div>
                                {info.sublabels && info.sublabels.length > 0 && (
                                  <div style={{
                                    fontSize: '11px', color: 'var(--g400)', marginTop: '5px',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                  <div style={{ textTransform: 'capitalize' }}>  {info.sublabels.map(s => s.toLowerCase()).slice(0, 3).join(', ')}{info.count > 3 ? '…' : ''}</div>
                                  </div>
                                )|| (<div style={{ textTransform: 'capitalize', fontSize: '11px', color: 'var(--g400)', marginTop: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>-</div>)}
                                <div style={{ marginTop: '8px' }}>
                                  <span className={`chip ${catChipClass(cat)}`}>{cat}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Search + filter row */}
                        <div className="flex g8 aic" style={{ marginBottom: '16px' }}>
                          <div style={{ position: 'relative', flex: '0 1 240px' }}>
                            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--g400)', pointerEvents: 'none' }}>🔍</span>
                            <input
                              className="inp"
                              placeholder="Search check type…"
                              value={alacarteSearch}
                              onChange={e => setAlacarteSearch(e.target.value)}
                              style={{ paddingLeft: '30px' }}
                            />
                          </div>
                          {alacarteCategories.length > 0 && (
                            <select
                              className="sel"
                              value={alacarteCategory}
                              onChange={e => setAlacarteCategory(e.target.value)}
                              style={{ fontSize: '12px', width: 'auto' }}
                            >
                              <option value="">All Categories</option>
                              {alacarteCategories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          )}
                          <div style={{ fontSize: '11.5px', color: 'var(--g600)', whiteSpace: 'nowrap', padding: '0 4px' }}>
                            {filteredAlacarte.length} check{filteredAlacarte.length !== 1 ? 's' : ''}
                          </div>
                        </div>

                        {/* Table */}
                        <div className="twrap">
                          <table>
                            <thead>
                              <tr>
                                <th style={{ width: '42%' }}>Check Type</th>
                                <th>Price</th>
                                <th>Country</th>
                                <th>Currency</th>
                                {alacarteCategories.length > 0 && <th>Category</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredAlacarte.length === 0 ? (
                                <tr>
                                  <td colSpan={alacarteCategories.length > 0 ? 5 : 4} style={{ padding: '40px', textAlign: 'center' }}>
                                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--g600)' }}>
                                      {pricingAlacarteWithCat.length === 0 ? 'No A la Carte pricing configured yet.' : 'No checks found'}
                                    </div>
                                    {pricingAlacarteWithCat.length > 0 && <div style={{ fontSize: '11.5px', color: 'var(--g400)', marginTop: '4px' }}>Try a different search term</div>}
                                  </td>
                                </tr>
                              ) : (
                                filteredAlacarte.map((p, i) => (
                                  <tr key={i}>
                                    <td className="td1" style={{ textTransform: 'capitalize' }}>{p.checktype.toLowerCase() || '—'}</td>
                                    <td>
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                                        background: 'var(--or-lt)', border: '1px solid rgba(245,166,35,.4)',
                                        borderRadius: '50px', fontSize: '12px', fontWeight: 700, color: 'var(--or-dk)',
                                      }}>
                                        ₹{p.price || '—'}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.countryname || '—'}</td>
                                    <td>
                                      <span style={{
                                        display: 'inline-block', background: 'var(--g100)', border: '1px solid var(--g200)',
                                        color: 'var(--g600)', fontSize: '10.5px', fontWeight: 600,
                                        padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace',
                                      }}>
                                        {p.currency || '—'}
                                      </span>
                                    </td>
                                    {alacarteCategories.length > 0 && (
                                      <td>
                                        {p.category
                                          ? <span className={`chip ${catChipClass(p.category)}`}>{p.category}</span>
                                          : <span style={{ color: 'var(--g400)', fontSize: '12px' }}>—</span>}
                                      </td>
                                    )}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div style={{ borderTop: '1px solid var(--g100)', margin: '14px 0' }} />
                        <div className="alert a-or">
                          <div className="aico">ℹ️</div>
                          <div className="abody">
                            <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '2px', fontSize: '12px' }}>Pricing configured by SwiftCheq</div>
                            Rates above reflect your negotiated contract pricing. Contact your account manager for any revisions.
                          </div>
                        </div>
                      </>
                    )}

                    {/* ── Package ── */}
                    {pricingSubTab === 1 && (
                      <>
                        {/* Stat cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
                          {[
                            { icon: '📦', bg: 'var(--or-lt)', label: 'Total packages', val: packageGroups.length, note: 'Active' },
                            { icon: '✅', bg: 'var(--gr-lt)', label: 'Check types',     val: totalPkgCheckTypes, note: 'Across all packages' },
                            { icon: '💳', bg: 'var(--bl-lt)', label: 'Total value',     val: `₹${totalPkgValue.toLocaleString('en-IN')}`, note: 'Combined' },
                          ].map(s => (
                            <div key={s.label} style={{ background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 'var(--rs)', padding: '13px 15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: 38, height: 38, borderRadius: 9, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{s.icon}</div>
                              <div>
                                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--g600)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '2px' }}>{s.label}</div>
                                <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--nv)', lineHeight: 1 }}>{s.val}</div>
                                <div style={{ fontSize: '10.5px', color: 'var(--g400)', marginTop: '2px' }}>{s.note}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Package cards */}
                        {packageGroups.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--g400)' }}>
                            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--g600)' }}>No packages configured</div>
                            <div style={{ fontSize: '11.5px', marginTop: '4px' }}>Contact your account manager to set up packages</div>
                          </div>
                        ) : (
                          packageGroups.map((pkg, pi) => (
                            <div key={pi} style={{ border: '1px solid var(--g200)', borderRadius: 'var(--r)', overflow: 'hidden', marginBottom: '14px' }}>
                              {/* Package card header */}
                              <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '13px 18px',
                                background: 'linear-gradient(90deg, var(--g50) 0%, var(--or-xlt) 100%)',
                                borderBottom: '1px solid var(--g200)',
                              }}>
                                <div>
                                  <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--nv)' }}>{pkg.name || '—'}</div>
                                  <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '2px' }}>
                                    {pkg.checks.length} check type{pkg.checks.length !== 1 ? 's' : ''} · {pkg.countryname || 'India'}
                                  </div>
                                </div>
                                <div className="flex aic g8">
                                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--g600)', background: 'var(--wh)', border: '1px solid var(--g200)', borderRadius: '50px', padding: '2px 10px' }}>
                                    🌏 {pkg.countryname || 'India'}
                                  </span>
                                  <span className="chip cg">Active</span>
                                  <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--or-dk)' }}>₹{pkg.price || '—'}</span>
                                </div>
                              </div>

                              {/* Checks table */}
                              <div className="twrap">
                                <table>
                                  <thead>
                                    <tr>
                                      <th style={{ width: '40%' }}>Check Type</th>
                                      <th>Qty Used</th>
                                      {/* <th>Qty Remaining</th> */}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pkg.checks.map((c, ki) => {
                                      const ctSetting = checkTypeSettings.find(ct => ct.checktypelbl === c.checktype);
                                      const hasTenure = c.has_tenure === 'TRUE' || ctSetting?.has_tenure === 'TRUE';
                                      const hasTenureSla = c.has_tenure_sla === 'TRUE';
                                      return (
                                        <tr key={ki}>
                                          <td className="td1" style={{ textTransform: 'capitalize' }}>{c.checktype.toLowerCase() || '—'}</td>
                                          <td>
                                            <QtyBubble val={c.chktypepkgcount} />
                                            {hasTenure && <TenureChip />}
                                          </td>
                                          {/* <td>
                                            <QtyBubble val={c.sla_value} />
                                            {hasTenureSla && <TenureChip />}
                                          </td> */}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))
                        )}

                        <div className="alert a-bl" style={{ marginTop: '12px' }}>
                          <div className="aico">ℹ️</div>
                          <div className="abody">
                            <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '2px', fontSize: '12px' }}>Tenure-based checks</div>
                            Checks marked <strong>Is Tenure</strong> are unlimited within your contract tenure period — they do not deduct per-use credits.
                          </div>
                        </div>
                      </>
                    )}

                    {/* ── Extra Checks ── */}
                    {pricingSubTab === 2 && (
                      <>
                        {/* Stat cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
                          {[
                            { icon: '➕', bg: 'var(--or-lt)', label: 'Extra checks', val: pricingExtraChecks.length, note: 'Add-on types' },
                            { icon: '🌍', bg: '#e0f2fe',      label: 'Countries',    val: extraCountries.length || '—', note: extraCountries.length > 0 ? `${extraCountries[0]}${extraCountries.length > 1 ? ` +${extraCountries.length - 1}` : ''}` : 'Available' },
                            { icon: '💱', bg: 'var(--bl-lt)', label: 'Currency',     val: extraCurrencies[0] || '—', note: extraCurrencies.length > 1 ? `+ ${extraCurrencies.length - 1} more` : 'Indian Rupee' },
                          ].map(s => (
                            <div key={s.label} style={{ background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 'var(--rs)', padding: '13px 15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: 38, height: 38, borderRadius: 9, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{s.icon}</div>
                              <div>
                                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--g600)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '2px' }}>{s.label}</div>
                                <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--nv)', lineHeight: 1 }}>{s.val}</div>
                                <div style={{ fontSize: '10.5px', color: 'var(--g400)', marginTop: '2px' }}>{s.note}</div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="twrap">
                          <table>
                            <thead>
                              <tr>
                                <th style={{ width: '42%' }}>Check Type</th>
                                <th>Price</th>
                                <th>Country</th>
                                <th>Currency</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pricingExtraChecks.length === 0 ? (
                                <tr>
                                  <td colSpan={4} style={{ textAlign: 'center', padding: '28px', color: 'var(--g400)' }}>No extra checks configured yet.</td>
                                </tr>
                              ) : (
                                pricingExtraChecks.map((p, i) => (
                                  <tr key={i}>
                                    <td className="td1" style={{ textTransform: 'capitalize' }}>{p.extracheckname.toLowerCase() || '—'}</td>
                                    <td>
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                                        background: 'var(--or-lt)', border: '1px solid rgba(245,166,35,.4)',
                                        borderRadius: '50px', fontSize: '12px', fontWeight: 700, color: 'var(--or-dk)',
                                      }}>
                                        ₹{p.price || '—'}
                                      </span>
                                    </td>
                                    <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.countryname || '—'}</td>
                                    <td>
                                      <span style={{
                                        display: 'inline-block', background: 'var(--g100)', border: '1px solid var(--g200)',
                                        color: 'var(--g600)', fontSize: '10.5px', fontWeight: 600,
                                        padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace',
                                      }}>
                                        {p.currency || '—'}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div style={{ borderTop: '1px solid var(--g100)', margin: '14px 0' }} />
                        <div className="alert a-bl">
                          <div className="aico">ℹ️</div>
                          <div className="abody">
                            <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '2px', fontSize: '12px' }}>Pricing is configured by cFIRST</div>
                            Extra checks are add-ons outside your standard package. Contact your account manager to add more check types or adjust pricing.
                          </div>
                        </div>
                        <div style={{ marginTop: '12px' }}>
                          <button className="btn bout bsm">📞 Request Additional Checks</button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Agreement / SLA */}
            {activeTab === 4 && (
              <div className="scontent">
                {loading ? <LoadingBlock /> : (
                  <>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)', marginBottom: '16px' }}>Attachments</div>
                    {/* Agreement / SLA */}

                    {/* <div className="flex jb aic mt20" style={{ background: 'var(--g50)', borderRadius: 'var(--rs)', padding: '12px 14px', border: '1px solid var(--g200)' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '13px' }}>Prepaid Agreement</div>
                        <div style={{ fontSize: '11.5px', color: 'var(--g600)', marginTop: '2px' }}>Download and review your signed agreement</div>
                      </div>
                      <button className="btn bout" onClick={downloadAgreement} disabled={downloading}>
                        {downloading ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Downloading…</> : '⬇ Download Agreement'}
                      </button>
                    </div> */}

                    <div style={{ marginTop: '20px' }}>
                      {/* <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '12px' }}>Attachments</div> */}
                      <div style={{ border: '1px solid var(--g200)', borderRadius: 'var(--rs)', overflow: 'hidden' }}>
                        {CONTRACT_ATTACHMENTS.map((type, i) => {
                          const existing = existingAttachments.find(a => a.attachment_type === type);
                          return (
                            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderBottom: i < CONTRACT_ATTACHMENTS.length - 1 ? '1px solid var(--g100)' : 'none', background: i % 2 === 0 ? 'var(--g50)' : '' }}>
                              <div style={{ width: '140px', fontSize: '13px', fontWeight: 600, color: 'var(--g700)', flexShrink: 0 }}>{type}</div>
                              {existing ? (
                                <button type="button" className="btn bout bsm" onClick={() => downloadExistingAttachment(existing.attachment_name)}>
                                  ⬇ {existing.attachment_name}
                                </button>
                              ) : (
                                <span style={{ fontSize: '12px', color: 'var(--g400)' }}>Not uploaded</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                  </>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

// ── Sub-components ────────────────────────────────────────────

function LoadingBlock() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
      <span className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );
}

function BillingField({ label, req, value, onChange, error, readOnly }) {
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      <input className="inp" value={value} readOnly={readOnly}
        onChange={readOnly ? undefined : e => onChange(e.target.value)}
        style={!readOnly && error ? { borderColor: 'var(--rd)' } : {}} />
      {!readOnly && error && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
    </div>
  );
}

function NAField({ label, req, value, naChecked, naLabel, onChange, onNA, error, readOnly }) {
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      <input className="inp" value={value} readOnly={readOnly || naChecked}
        onChange={readOnly || naChecked ? undefined : e => onChange(e.target.value)}
        style={{ ...(naChecked ? { background: 'var(--g100)', color: 'var(--g400)' } : {}), ...(!readOnly && error && !naChecked ? { borderColor: 'var(--rd)' } : {}) }} />
      {!readOnly && error && !naChecked && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--g600)', cursor: readOnly ? 'default' : 'pointer', marginTop: '4px' }}>
        <input type="checkbox" checked={naChecked} disabled={readOnly} onChange={readOnly ? undefined : e => onNA(e.target.checked)} />
        {naLabel}
      </label>
    </div>
  );
}

function QtyBubble({ val }) {
  const n = parseInt(val, 10);
  const hasVal = !isNaN(n) && n > 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: '50%', fontSize: '11px', fontWeight: 700,
      background: hasVal ? 'var(--or)' : 'var(--g100)',
      border: hasVal ? 'none' : '1px solid var(--g200)',
      color: hasVal ? 'var(--nv)' : 'var(--g400)',
    }}>
      {val != null ? val : '—'}
    </span>
  );
}

function TenureChip() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      fontSize: '10px', fontWeight: 600,
      background: 'var(--pu-lt)', color: '#5B21B6',
      border: '1px solid rgba(124,58,237,.2)',
      borderRadius: '50px', padding: '2px 7px', marginLeft: '5px',
    }}>
      🕐 yrs
    </span>
  );
}
